// DWOP decoder for REX2 SDAT chunks.
//
// Faithful port of REX Shared Library.dll v1.9.2:
//   - decodeMono   <- FUN_1800317d0  (cVar1 == 0)
//   - decodeStereo <- FUN_180031d70  (cVar1 != 0, two interleaved streams)
//
// Verified bit-exact against the DLL via Frida instrumentation (the DLL's
// internal FUN_1800317d0 output matches decodeMono exactly).
//
// Key facts:
//   - SDAT is a raw entropy bitstream of 32-bit BIG-ENDIAN words, MSB-first.
//   - Adaptive escalating Golomb/Rice coding with 5 fixed-order polynomial
//     predictors (orders 0..4) selected per-sample by smallest magnitude EMA.
//   - Initial predictor state = 0, initial averages = 0xa00. All internal
//     state is kept in a "doubled" domain; output sample = acc >> 1.

const u32 = (x) => x >>> 0;
const s32 = (x) => x | 0;
const absFold = (x) => (s32(x) >= 0 ? u32(x) : (~s32(x)) >>> 0);

class WordReader {
  // Reads 32-bit big-endian words. The DLL byte-swaps SDAT words on load
  // (refill FUN_180041980), which is equivalent to big-endian here.
  constructor(buf) {
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    this.nwords = (buf.byteLength / 4) | 0;
    this.pos = 0;
  }
  next() {
    if (this.pos < this.nwords) {
      const w = this.view.getUint32(this.pos * 4, false); // big-endian
      this.pos++;
      return w >>> 0;
    }
    return 0; // zero-fill past the end (decode should stop first)
  }
}

/**
 * Decode a mono DWOP stream.
 * @param {Uint8Array} data   SDAT chunk bytes.
 * @param {number} numSamples number of samples to decode.
 * @returns {Int32Array} decoded PCM samples (16-bit range).
 */
function decodeMono(data, numSamples) {
  const reader = new WordReader(data);
  const out = new Int32Array(numSamples);

  // --- initial state (from FUN_18002d200 init loop) ---
  let iVar24 = 0;          // k
  let uVar15 = 2;          // m
  let acc = 0;             // local_res10[0] = h0*2
  let uVar23 = 0;          // d4 (doubled)
  let uVar13 = 0xa00, uVar20 = 0xa00, uVar5 = 0xa00, uVar6 = 0xa00, uVar14 = 0xa00;
  let uVar2 = 0, uVar3 = 0, uVar4 = 0; // d1,d2,d3 (doubled)
  let iVar16 = 0;          // bitpos
  let uVar22 = 0;          // bit buffer

  // initial mode selection
  let uVar1 = uVar20;
  if (uVar13 <= uVar20) uVar1 = uVar13;
  let cVar25 = uVar20 < uVar13 ? 1 : 0;
  if (uVar5 < uVar1) { cVar25 = 2; uVar1 = uVar5; }
  if (uVar6 < uVar1) { cVar25 = 3; uVar1 = uVar6; }
  if (uVar14 < uVar1) { cVar25 = 4; uVar1 = uVar14; }

  for (let n = 0; n < numSamples; n++) {
    let iVar12 = 0;
    let iVar17 = 7;
    uVar1 = u32(((uVar1 + 0xc) * 3) >>> 7); // rem_base seed

    // ---- unary / escalating-Golomb prefix ----
    for (;;) {
      uVar23 = uVar1;
      let iVar18 = s32(uVar22);
      iVar16 -= 1;
      if (iVar16 < 0) { iVar18 = s32(reader.next()); iVar16 = 0x1f; }
      uVar1 = u32(iVar18 * 2);
      uVar22 = uVar1;
      if (iVar18 < 0) break;
      iVar12 = iVar12 + uVar23;
      iVar18 = iVar17 - 1;
      iVar17 = iVar18;
      if (iVar18 === 0) iVar17 = 7;
      uVar1 = u32(uVar23 * 4);
      if (iVar18 !== 0) uVar1 = uVar23;
    }

    // ---- adapt m (uVar15) and k (iVar24) against rem_base (uVar23) ----
    if (uVar23 < uVar15) {
      while (uVar23 < (uVar15 >>> 1)) { iVar24 -= 1; uVar15 = uVar15 >>> 1; }
    } else {
      for (;;) {
        iVar24 += 1;
        uVar15 = u32(uVar15 * 2);
        if (uVar15 === 0) throw new Error('DWOP: rice parameter overflow');
        if (!(uVar15 <= uVar23)) break;
      }
    }

    // ---- remainder: read iVar24 bits ----
    let uVar19 = 0;
    if (iVar24 !== 0) {
      const sh = (0x20 - iVar24) & 0x1f;
      uVar19 = u32(uVar1 >>> sh);
      uVar22 = u32(uVar1 << (iVar24 & 0x1f));
      const iVar17b = iVar16 - iVar24;
      iVar16 = iVar17b;
      if (iVar17b < 0) {
        uVar1 = u32(reader.next());
        iVar16 = iVar17b + 0x20;
        uVar19 = u32(uVar19 | (uVar1 >>> (iVar16 & 0x1f)));
        uVar22 = u32(uVar1 << ((-iVar17b) & 0x1f));
      }
    }

    // ---- adjusted-binary correction ----
    const thresh = u32(uVar15 - uVar23);
    if (s32(u32(uVar19 - thresh)) > -1) {
      iVar16 -= 1;
      if (iVar16 < 0) { uVar22 = u32(reader.next()); iVar16 = 0x1f; }
      uVar19 = u32((uVar19 * 2 - thresh) + (uVar22 >>> 0x1f));
      uVar22 = u32(s32(uVar22) * 2);
    }

    // ---- combine + zigzag fold ----
    uVar19 = u32(uVar19 + iVar12);
    uVar1 = (uVar19 & 1) === 0 ? uVar19 : (~uVar19) >>> 0;

    // ---- predictor reconstruction (mode cVar25) ----
    let local_res8, local_198;
    if (cVar25 === 1) {
      local_res8 = u32(uVar1 - uVar2);
      local_198 = u32(local_res8 - uVar3);
      uVar23 = u32(local_198 - uVar4);
      uVar19 = uVar1;
      acc = u32(acc + uVar1);
    } else if (cVar25 === 2) {
      uVar19 = u32(uVar2 + uVar1);
      uVar23 = u32((uVar1 - uVar3) - uVar4);
      local_res8 = uVar1;
      acc = u32(acc + uVar2 + uVar1);
      local_198 = u32(uVar1 - uVar3);
    } else if (cVar25 === 3) {
      local_res8 = u32(uVar3 + uVar1);
      uVar19 = u32(uVar2 + local_res8);
      uVar23 = u32(uVar1 - uVar4);
      acc = u32(acc + uVar2 + local_res8);
      local_198 = uVar1;
    } else if (cVar25 === 4) {
      local_198 = u32(uVar4 + uVar1);
      local_res8 = u32(uVar3 + local_198);
      uVar19 = u32(uVar2 + local_res8);
      uVar23 = uVar1;
      acc = u32(acc + uVar2 + local_res8);
    } else { // mode 0
      uVar19 = u32(uVar1 - acc);
      local_res8 = u32(uVar19 - uVar2);
      local_198 = u32(local_res8 - uVar3);
      uVar23 = u32(local_198 - uVar4);
      acc = uVar1;
    }

    out[n] = s32(acc) >> 1;

    // ---- EMA magnitude updates + mode reselection ----
    let a = absFold(acc);
    uVar13 = u32(uVar13 + (a - (uVar13 >>> 5)));
    a = absFold(uVar19);
    uVar20 = u32(uVar20 + (a - (uVar20 >>> 5)));
    uVar1 = uVar20;
    if (uVar13 <= uVar20) uVar1 = uVar13;
    a = absFold(local_res8);
    uVar5 = u32(uVar5 + (a - (uVar5 >>> 5)));
    cVar25 = uVar20 < uVar13 ? 1 : 0;
    if (uVar5 < uVar1) { cVar25 = 2; uVar1 = uVar5; }
    a = absFold(local_198);
    uVar6 = u32(uVar6 + (a - (uVar6 >>> 5)));
    if (uVar6 < uVar1) { cVar25 = 3; uVar1 = uVar6; }
    a = absFold(uVar23);
    uVar14 = u32(uVar14 + (a - (uVar14 >>> 5)));
    uVar2 = uVar19;
    uVar3 = local_res8;
    uVar4 = local_198;
    if (uVar14 < uVar1) { cVar25 = 4; uVar1 = uVar14; }
  }

  return out;
}

/**
 * Decode a stereo (dual-stream) DWOP block. Port of FUN_180031d70.
 * Two interleaved predictor streams; channel 0 = acc_a>>1, channel 1 =
 * (acc_a + acc_b)>>1 (mid/side decorrelation). Verified bit-exact against the
 * DLL via Frida (120Stereo: 2048 frames matched).
 * @param {Uint8Array} data
 * @param {number} numFrames number of stereo frames to decode.
 * @returns {Int32Array[]} [left, right]
 */
function decodeStereo(data, numFrames) {
  const reader = new WordReader(data);
  const left = new Int32Array(numFrames);
  const right = new Int32Array(numFrames);

  // channel 0 (A) state
  let kA = 0, mA = 2;
  let accA = 0, d4A = 0;
  let a0 = 0xa00, a1 = 0xa00, a2 = 0xa00, a3 = 0xa00, a4 = 0xa00;
  let d1A = 0, d2A = 0, d3A = 0;
  // channel 1 (B) state
  let kB = 0, mB = 2;
  let accB = 0, d4B = 0;
  let b0 = 0xa00, b1 = 0xa00, b2 = 0xa00, b3 = 0xa00, b4 = 0xa00;
  let d1B = 0, d2B = 0, d3B = 0;

  let bitpos = 0, buf = 0;

  let minA = a0 <= a1 ? a0 : a1;
  let cA = a1 < a0 ? 1 : 0;
  if (a2 < minA) { cA = 2; minA = a2; }
  if (a3 < minA) { cA = 3; minA = a3; }
  if (a4 < minA) { cA = 4; minA = a4; }
  let minB = b0 <= b1 ? b0 : b1;
  let cB = b1 < b0 ? 1 : 0;
  if (b2 < minB) { cB = 2; minB = b2; }
  if (b3 < minB) { cB = 3; minB = b3; }
  if (b4 < minB) { cB = 4; minB = b4; }

  const decodeSymbol = (seedMin, k, m) => {
    // returns { fold, kNew, mNew } and consumes bits from the shared stream
    let iVar12 = 0, iVar17 = 7;
    let uVar1 = u32(((seedMin + 0xc) * 3) >>> 7);
    let uVar23;
    for (;;) {
      uVar23 = uVar1;
      let iVar18 = s32(buf);
      bitpos -= 1;
      if (bitpos < 0) { iVar18 = s32(reader.next()); bitpos = 0x1f; }
      uVar1 = u32(iVar18 * 2);
      buf = uVar1;
      if (iVar18 < 0) break;
      iVar12 += uVar23;
      iVar18 = iVar17 - 1; iVar17 = iVar18;
      if (iVar18 === 0) iVar17 = 7;
      uVar1 = u32(uVar23 * 4);
      if (iVar18 !== 0) uVar1 = uVar23;
    }
    if (uVar23 < m) {
      while (uVar23 < (m >>> 1)) { k -= 1; m = m >>> 1; }
    } else {
      for (;;) { k += 1; m = u32(m * 2); if (m === 0) throw new Error('DWOP overflow'); if (!(m <= uVar23)) break; }
    }
    let uVar19 = 0;
    if (k !== 0) {
      const sh = (0x20 - k) & 0x1f;
      uVar19 = u32(uVar1 >>> sh);
      buf = u32(uVar1 << (k & 0x1f));
      const t = bitpos - k; bitpos = t;
      if (t < 0) {
        uVar1 = u32(reader.next());
        bitpos = t + 0x20;
        uVar19 = u32(uVar19 | (uVar1 >>> (bitpos & 0x1f)));
        buf = u32(uVar1 << ((-t) & 0x1f));
      }
    }
    const thresh = u32(m - uVar23);
    if (s32(u32(uVar19 - thresh)) > -1) {
      bitpos -= 1;
      if (bitpos < 0) { buf = u32(reader.next()); bitpos = 0x1f; }
      uVar19 = u32((uVar19 * 2 - thresh) + (buf >>> 0x1f));
      buf = u32(s32(buf) * 2);
    }
    uVar19 = u32(uVar19 + iVar12);
    const fold = (uVar19 & 1) === 0 ? uVar19 : (~uVar19) >>> 0;
    return { fold, k, m };
  };

  for (let n = 0; n < numFrames; n++) {
    // ---- channel A ----
    let r = decodeSymbol(minA, kA, mA); kA = r.k; mA = r.m;
    let fold = r.fold, res8, l198, d1n, d4n;
    if (cA === 1) { res8 = u32(fold - d1A); l198 = u32(res8 - d2A); d4A = u32(l198 - d3A); d1n = fold; accA = u32(accA + fold); }
    else if (cA === 2) { d1n = u32(d1A + fold); d4A = u32((fold - d2A) - d3A); res8 = fold; accA = u32(accA + d1A + fold); l198 = u32(fold - d2A); }
    else if (cA === 3) { res8 = u32(d2A + fold); d1n = u32(d1A + res8); d4A = u32(fold - d3A); accA = u32(accA + d1A + res8); l198 = fold; }
    else if (cA === 4) { l198 = u32(d3A + fold); res8 = u32(d2A + l198); d1n = u32(d1A + res8); d4A = fold; accA = u32(accA + d1A + res8); }
    else { d1n = u32(fold - accA); res8 = u32(d1n - d1A); l198 = u32(res8 - d2A); d4A = u32(l198 - d3A); accA = fold; }
    left[n] = s32(accA) >> 1;
    let av = absFold(accA); a0 = u32(a0 + (av - (a0 >>> 5)));
    av = absFold(d1n); a1 = u32(a1 + (av - (a1 >>> 5)));
    minA = a0 <= a1 ? a0 : a1; cA = a1 < a0 ? 1 : 0;
    av = absFold(res8); a2 = u32(a2 + (av - (a2 >>> 5)));
    if (a2 < minA) { cA = 2; minA = a2; }
    av = absFold(l198); a3 = u32(a3 + (av - (a3 >>> 5)));
    if (a3 < minA) { cA = 3; minA = a3; }
    av = absFold(d4A); a4 = u32(a4 + (av - (a4 >>> 5)));
    d1A = d1n; d2A = res8; d3A = l198;
    if (a4 < minA) { cA = 4; minA = a4; }

    // ---- channel B ----
    r = decodeSymbol(minB, kB, mB); kB = r.k; mB = r.m;
    fold = r.fold;
    let res8b, l198b, d1nb;
    if (cB === 1) { res8b = u32(fold - d1B); l198b = u32(res8b - d2B); d4B = u32(l198b - d3B); d1nb = fold; accB = u32(accB + fold); }
    else if (cB === 2) { d1nb = u32(d1B + fold); d4B = u32((fold - d2B) - d3B); res8b = fold; accB = u32(accB + d1B + fold); l198b = u32(fold - d2B); }
    else if (cB === 3) { res8b = u32(d2B + fold); d1nb = u32(d1B + res8b); d4B = u32(fold - d3B); accB = u32(accB + d1B + res8b); l198b = fold; }
    else if (cB === 4) { l198b = u32(d3B + fold); res8b = u32(d2B + l198b); d1nb = u32(d1B + res8b); d4B = fold; accB = u32(accB + d1B + res8b); }
    else { d1nb = u32(fold - accB); res8b = u32(d1nb - d1B); l198b = u32(res8b - d2B); d4B = u32(l198b - d3B); accB = fold; }
    right[n] = s32(u32(accA + accB)) >> 1;
    av = absFold(accB); b0 = u32(b0 + (av - (b0 >>> 5)));
    av = absFold(d1nb); b1 = u32(b1 + (av - (b1 >>> 5)));
    minB = b0 <= b1 ? b0 : b1; cB = b1 < b0 ? 1 : 0;
    av = absFold(res8b); b2 = u32(b2 + (av - (b2 >>> 5)));
    if (b2 < minB) { cB = 2; minB = b2; }
    av = absFold(l198b); b3 = u32(b3 + (av - (b3 >>> 5)));
    if (b3 < minB) { cB = 3; minB = b3; }
    av = absFold(d4B); b4 = u32(b4 + (av - (b4 >>> 5)));
    d1B = d1nb; d2B = res8b; d3B = l198b;
    if (b4 < minB) { cB = 4; minB = b4; }
  }

  return [left, right];
}

// ============================================================================
// DWOP Encoder — inverse of the decoder
// ============================================================================
//
// The encoder reverses the decoder's bitstream:
//   1. For each sample, compute the prediction residual based on the current
//      mode (0..4), same as the decoder's reconstruction.
//   2. Zigzag-fold the residual to an unsigned code.
//   3. Encode the code as an escalating Golomb prefix (0-bits) + stop bit (1),
//      then a k-bit Rice remainder with adjusted-binary correction.
//   4. Update all predictor state identically to the decoder.
//
// The greedy Golomb prefix (subtract base while code >= base) always produces
// a remainder < base, which is encodable since m >= base after adaptation.

class BitWriter {
  constructor() {
    this.words = [];
    this.currentWord = 0;
    this.bitPos = 31; // MSB first, matching decoder's bit order
  }

  writeBit(bit) {
    if (bit) this.currentWord |= (1 << this.bitPos);
    this.bitPos--;
    if (this.bitPos < 0) {
      this.words.push(this.currentWord >>> 0);
      this.currentWord = 0;
      this.bitPos = 31;
    }
  }

  writeBits(value, numBits) {
    if (numBits <= 0) return;
    for (let i = numBits - 1; i >= 0; i--) {
      this.writeBit((value >>> i) & 1);
    }
  }

  flush() {
    if (this.bitPos < 31) {
      this.words.push(this.currentWord >>> 0);
      this.currentWord = 0;
      this.bitPos = 31;
    }
    const buf = new ArrayBuffer(this.words.length * 4);
    const view = new DataView(buf);
    for (let i = 0; i < this.words.length; i++) {
      view.setUint32(i * 4, this.words[i], false); // big-endian
    }
    return new Uint8Array(buf);
  }
}

// Zigzag encode: maps unsigned doubled-domain residual to unsigned code.
// Decoder: fold = (code & 1) == 0 ? code : ~code
// Encoder: code = (residual >= 0) ? residual : ~residual
function zigzagEncode(fold) {
  if (s32(fold) >= 0) return u32(fold);
  return u32((~fold) >>> 0);
}

// Encode one DWOP symbol: escalating Golomb prefix + Rice remainder.
// Returns updated { k, m }.
function encodeSymbol(writer, value, k, m, seedMin) {
  let base = u32(((seedMin + 0xc) * 3) >>> 7);
  if (base === 0) base = 1;

  let groupCount = 0;

  // Greedily subtract base from value, writing 0-bits.
  // Every 7 zero-bits, base escalates (×4).
  while (s32(value) >= s32(base) && base > 0) {
    writer.writeBit(0);
    value = u32(value - base);
    groupCount++;
    if (groupCount === 7) {
      base = u32(base * 4);
      if (base === 0) throw new Error('DWOP encode: base overflow');
      groupCount = 0;
    }
  }

  // Write stop bit
  writer.writeBit(1);

  // Adapt k/m based on final base (same as decoder)
  if (base < m) {
    while (base < (m >>> 1)) { k--; m = m >>> 1; }
  } else {
    for (;;) {
      k++;
      m = u32(m * 2);
      if (m === 0) throw new Error('DWOP encode: rice parameter overflow');
      if (!(m <= base)) break;
    }
  }

  // Encode remainder with adjusted binary
  const thresh = u32(m - base);
  if (s32(u32(value - thresh)) < 0) {
    // remainder < thresh: simple k-bit encoding
    writer.writeBits(value, k);
  } else {
    // remainder >= thresh: adjusted binary (k bits + 1 extra bit)
    // Decoder: corrected = raw * 2 - thresh + bit
    // Encoder: raw = (remainder + thresh) >> 1, bit = (remainder + thresh) & 1
    const adjusted = u32(value + thresh);
    const bit = adjusted & 1;
    const raw = adjusted >>> 1;
    writer.writeBits(raw, k);
    writer.writeBit(bit);
  }

  return { k, m };
}

// Helper: compute residual, update predictor state for one channel.
// Returns { residual, res8, l198, d1n, d4n, acc_new }.
function predictAndUpdate(targetAcc, mode, acc, d1, d2, d3) {
  let residual, res8, l198, d1n, d4n;

  if (mode === 1) {
    residual = u32(targetAcc - acc);
    res8 = u32(residual - d1);
    l198 = u32(res8 - d2);
    d4n = u32(l198 - d3);
    d1n = residual;
    acc = u32(acc + residual);
  } else if (mode === 2) {
    residual = u32(targetAcc - acc - d1);
    d1n = u32(d1 + residual);
    d4n = u32((residual - d2) - d3);
    res8 = residual;
    acc = u32(acc + d1 + residual);
    l198 = u32(residual - d2);
  } else if (mode === 3) {
    residual = u32(targetAcc - acc - d1 - d2);
    res8 = u32(d2 + residual);
    d1n = u32(d1 + res8);
    d4n = u32(residual - d3);
    acc = u32(acc + d1 + res8);
    l198 = residual;
  } else if (mode === 4) {
    residual = u32(targetAcc - acc - d1 - d2 - d3);
    l198 = u32(d3 + residual);
    res8 = u32(d2 + l198);
    d1n = u32(d1 + res8);
    d4n = residual;
    acc = u32(acc + d1 + res8);
  } else {
    // mode 0: prediction = 0, residual = target
    residual = targetAcc;
    d1n = u32(residual - acc);
    res8 = u32(d1n - d1);
    l198 = u32(res8 - d2);
    d4n = u32(l198 - d3);
    acc = residual;
  }

  return { residual, res8, l198, d1n, d4n, accNew: acc };
}

// Helper: update EMA magnitudes and select mode for next sample.
// Returns { minAvg, mode, a0..a4, d1..d3, d4 }.
function updateEMA(acc, d1n, res8, l198, d4n, d1, d2, d3, a0, a1, a2, a3, a4) {
  let av = absFold(acc);
  a0 = u32(a0 + (av - (a0 >>> 5)));
  av = absFold(d1n);
  a1 = u32(a1 + (av - (a1 >>> 5)));
  let minAvg = a0 <= a1 ? a0 : a1;
  let mode = a1 < a0 ? 1 : 0;
  av = absFold(res8);
  a2 = u32(a2 + (av - (a2 >>> 5)));
  if (a2 < minAvg) { mode = 2; minAvg = a2; }
  av = absFold(l198);
  a3 = u32(a3 + (av - (a3 >>> 5)));
  if (a3 < minAvg) { mode = 3; minAvg = a3; }
  av = absFold(d4n);
  a4 = u32(a4 + (av - (a4 >>> 5)));
  d1 = d1n; d2 = res8; d3 = l198;
  if (a4 < minAvg) { mode = 4; minAvg = a4; }
  return { minAvg, mode, a0, a1, a2, a3, a4, d1, d2, d3 };
}

/**
 * Encode a mono DWOP stream.
 * @param {Int32Array} samples  PCM samples (16-bit range, -32768..32767)
 * @returns {Uint8Array} DWOP-compressed SDAT data
 */
function encodeMono(samples) {
  const writer = new BitWriter();

  let k = 0, m = 2;
  let acc = 0, d1 = 0, d2 = 0, d3 = 0, d4 = 0;
  let a0 = 0xa00, a1 = 0xa00, a2 = 0xa00, a3 = 0xa00, a4 = 0xa00;

  let minAvg = a0;
  let mode = 0;

  for (let n = 0; n < samples.length; n++) {
    const targetAcc = u32(samples[n] * 2);

    const pr = predictAndUpdate(targetAcc, mode, acc, d1, d2, d3);
    acc = pr.accNew;

    const code = zigzagEncode(pr.residual);
    const r = encodeSymbol(writer, code, k, m, minAvg);
    k = r.k; m = r.m;

    const ema = updateEMA(acc, pr.d1n, pr.res8, pr.l198, pr.d4n, d1, d2, d3, a0, a1, a2, a3, a4);
    minAvg = ema.minAvg; mode = ema.mode;
    a0 = ema.a0; a1 = ema.a1; a2 = ema.a2; a3 = ema.a3; a4 = ema.a4;
    d1 = ema.d1; d2 = ema.d2; d3 = ema.d3; d4 = pr.d4n;
  }

  return writer.flush();
}

/**
 * Encode a stereo DWOP stream (two interleaved predictor streams).
 * Decoder: left[n] = accA >> 1, right[n] = (accA + accB) >> 1
 * Encoder: targetAccA = left * 2, targetAccB = right * 2 - targetAccA
 * @param {Int32Array} left   Left channel PCM samples
 * @param {Int32Array} right  Right channel PCM samples
 * @returns {Uint8Array} DWOP-compressed SDAT data
 */
function encodeStereo(left, right) {
  const writer = new BitWriter();
  const numFrames = left.length;

  // Channel A state
  let kA = 0, mA = 2;
  let accA = 0, d1A = 0, d2A = 0, d3A = 0, d4A = 0;
  let a0 = 0xa00, a1 = 0xa00, a2 = 0xa00, a3 = 0xa00, a4 = 0xa00;
  let minA = a0, cA = 0;

  // Channel B state
  let kB = 0, mB = 2;
  let accB = 0, d1B = 0, d2B = 0, d3B = 0, d4B = 0;
  let b0 = 0xa00, b1 = 0xa00, b2 = 0xa00, b3 = 0xa00, b4 = 0xa00;
  let minB = b0, cB = 0;

  for (let n = 0; n < numFrames; n++) {
    // ---- Channel A: encodes left * 2 ----
    const targetAccA = u32(left[n] * 2);
    const prA = predictAndUpdate(targetAccA, cA, accA, d1A, d2A, d3A);
    accA = prA.accNew;
    const codeA = zigzagEncode(prA.residual);
    const rA = encodeSymbol(writer, codeA, kA, mA, minA);
    kA = rA.k; mA = rA.m;
    const emaA = updateEMA(accA, prA.d1n, prA.res8, prA.l198, prA.d4n, d1A, d2A, d3A, a0, a1, a2, a3, a4);
    minA = emaA.minAvg; cA = emaA.mode;
    a0 = emaA.a0; a1 = emaA.a1; a2 = emaA.a2; a3 = emaA.a3; a4 = emaA.a4;
    d1A = emaA.d1; d2A = emaA.d2; d3A = emaA.d3; d4A = prA.d4n;

    // ---- Channel B: encodes right * 2 - targetAccA ----
    const targetAccB = u32(right[n] * 2 - targetAccA);
    const prB = predictAndUpdate(targetAccB, cB, accB, d1B, d2B, d3B);
    accB = prB.accNew;
    const codeB = zigzagEncode(prB.residual);
    const rB = encodeSymbol(writer, codeB, kB, mB, minB);
    kB = rB.k; mB = rB.m;
    const emaB = updateEMA(accB, prB.d1n, prB.res8, prB.l198, prB.d4n, d1B, d2B, d3B, b0, b1, b2, b3, b4);
    minB = emaB.minAvg; cB = emaB.mode;
    b0 = emaB.a0; b1 = emaB.a1; b2 = emaB.a2; b3 = emaB.a3; b4 = emaB.a4;
    d1B = emaB.d1; d2B = emaB.d2; d3B = emaB.d3; d4B = prB.d4n;
  }

  return writer.flush();
}

module.exports = {
   decodeMono,
   decodeStereo,
   encodeMono,
   encodeStereo,
};
